import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ProgressBarTop from "./ProgressBarTop";
import ExplorationStepSidebar from "./ExplorationStepSidebar";

/**
 * Shared layout for Color Exploration flows: optional progress bar(s), gradient header, content area.
 *
 * If `stepSidebar` is provided, a gray vertical step overview is rendered on the left
 * (collapses on top on small screens) so the executor sees where they are in the flow.
 *
 * stepSidebar shape:
 *   {
 *     steps: [{ key, label, short? }, ...],
 *     currentIndex: number,
 *     iterationValue: number | string | null,
 *     iterationLabel?: string,
 *     populationLabel?: string | null,  // e.g. "Input collection (P0)"
 *     valueCaption?: string,
 *     loop?: { stepKeys, label, earlier } | null,  // steps repeated per iteration, see ExplorationStepSidebar
 *   }
 */
export default function ExplorationPageShell({
  title,
  subtitle,
  /** e.g. [{ current: 2, total: 10, name: "Exploration" }, { current: 1, total: 3, name: "Color - Resolution" }] */
  progressBars,
  /** Rendered in the gradient header row, right of the title (e.g. secondary actions). */
  headerTrailing,
  /** Rendered in the gradient header below the title row (e.g. status chips). */
  headerBadges,
  children,
  /** If true, children render below the header without a Paper card (e.g. wide charts). */
  noPaper = false,
  paperSx = {},
  contentBoxSx = {},
  /** The shell's width cap; a wide table (the Users page) asks for more. */
  maxWidth = null,
  /** Rendered below the Paper (e.g. nav actions), full width of the shell. Ignored when noPaper. */
  footerBelowPaper,
  footerBelowPaperSx = {},
  /** Optional left-side step overview. See file header for shape. */
  stepSidebar = null,
}) {
  const hasStepSidebar = stepSidebar && Array.isArray(stepSidebar.steps) && stepSidebar.steps.length > 0;

  const mainContent = (
    <>
      <Stack
        sx={{
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          alignSelf: "stretch",
          py: 2,
          borderRadius: 3,
          // Per-page header in a soft, light neutral so it reads as
          // "current page" context — clearly lighter than the persistent
          // exploration-wide ``ExplorationStepSidebar`` panel on the left.
          background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
          border: "1px solid #dde1e7",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ px: 2, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "flex-start" }}
            spacing={2}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {headerTrailing ? <Box sx={{ flexShrink: 0 }}>{headerTrailing}</Box> : null}
          </Stack>
          {headerBadges ? (
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mt: 2 }}>
              {headerBadges}
            </Stack>
          ) : null}
        </Box>
      </Stack>

      {noPaper ? (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            alignSelf: "stretch",
            boxSizing: "border-box",
            ...contentBoxSx,
          }}
        >
          {children}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mt: 2,
            mb: footerBelowPaper ? 0 : 2,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
            ...paperSx,
          }}
        >
          {children}
        </Paper>
      )}
      {!noPaper && footerBelowPaper ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mt: 2,
            mb: 3,
            boxSizing: "border-box",
            ...footerBelowPaperSx,
          }}
        >
          {footerBelowPaper}
        </Box>
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: maxWidth || (hasStepSidebar ? "1400px" : "1150px"),
        minWidth: 0,
        mx: "auto",
        px: 2,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      {progressBars && progressBars.length > 0 && (
        <Stack spacing={2} sx={{ mb: 2, width: "100%" }}>
          {progressBars.map((pb, i) => (
            <ProgressBarTop
              key={i}
              currentStepNumber={pb.current}
              totalStepNumber={pb.total}
              progressBarName={pb.name}
            />
          ))}
        </Stack>
      )}

      {hasStepSidebar ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: { xs: 2, md: 3 },
            width: "100%",
          }}
        >
          <ExplorationStepSidebar
            steps={stepSidebar.steps}
            currentIndex={stepSidebar.currentIndex}
            iterationValue={stepSidebar.iterationValue}
            iterationLabel={stepSidebar.iterationLabel}
            populationLabel={stepSidebar.populationLabel}
            valueCaption={stepSidebar.valueCaption}
            loop={stepSidebar.loop}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            {mainContent}
          </Box>
        </Box>
      ) : (
        mainContent
      )}
    </Box>
  );
}
